import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormGroup, Popup } from 'semantic-ui-react';
import { useEffect, useMemo, useState } from 'react';
import { SkupremeModal } from '../../components';
import {
  CenterDropdownStyle,
  CheckboxFormField,
  ControlledTextFormField,
  HoverBorderButton,
  MuteText,
  NumberFormField,
  StyledButton,
} from '../../elements';
import { CommonUtility, decimalMask, ToastMessage, ZoneService } from '../../utility';
import { RackPresetsHook } from '../../hooks';
import { useWarehouseDetails } from '../../screens/app/settings/warehouse';

const ZoneSchema = yup.object().shape({
  quantity: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : Number(originalValue),
    )
    .typeError('Quantity is required')
    .positive('Quantity must be greater than 0')
    .required('Quantity is required'),
  code: yup.string().when('quantity', (quantityVal, schema) => {
    const parsed = Number(quantityVal);
    return parsed === 1
      ? schema.required('Code is required')
      : schema.notRequired().nullable();
  }),
  length: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : Number(originalValue),
    )
    .typeError('Length is required')
    .positive('Length must be greater than 0')
    .required('Length is required'),
  width: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : Number(originalValue),
    )
    .typeError('Width is required')
    .positive('Width must be greater than 0')
    .required('Width is required'),
  createPreset: yup.boolean(),
});

export function CreateRackPopup({ onClose, zoneId, open, refreshData, aisle }) {
  const [loading, setLoading] = useState(false);
  const { warehouseData } = useWarehouseDetails();
  const { data: presetOptions, loading: presetLoading } = RackPresetsHook();
  const [preset, setPreset] = useState('');

  useEffect(() => {
    if (open) {
      reset({ quantity: 1 });
      setPreset('-1');
    }
  }, [open]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(ZoneSchema),
    shouldUnregister: true,
    defaultValues: {
      quantity: 1,
    },
  });
  const quantity = Number(watch('quantity') || 0);

  const onSubmit = async formData => {
    if (!warehouseData?.dimensions?.measurementUnit) {
      ToastMessage.error('No Measurement Unit found for warehouse');
      return;
    }
    try {
      setLoading(true);

      const payload = {
        type: 'mixed',
        quantity: formData.quantity,
        dimensions: {
          length: formData.length,
          width: formData.width,
          measurementUnit: warehouseData.dimensions.measurementUnit,
        },
      };

      if (formData.quantity === 1 && formData.code) {
        payload.code = formData.code;
      }

      if (preset !== '-1') {
        payload.presetId = preset;
      }

      if (aisle?._id) {
        payload.aisleId = aisle._id;
      }

      await ZoneService.addRacks(zoneId, payload);
      ToastMessage.success('Racks successfully created.');
      onClose();
      refreshData();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const isCreateNewRack = useMemo(() => preset === '-1', [preset]);

  useEffect(() => {
    if (CommonUtility.isValidArray(presetOptions)) {
      const option = presetOptions.find(x => x.value === preset)?.item;
      if (option) {
        reset({
          quantity: watch('quantity') || 1,
          type: option.type,
          length: option.dimensions?.length,
          width: option.dimensions?.width,
        });
      } else {
        reset({
          quantity: watch('quantity') || 1,
          type: '',
          length: 0,
          width: 0,
        });
      }
    }
  }, [preset]);

  return (
    <SkupremeModal
      size="mini"
      title={aisle?.code ? `Create Rack in ${aisle.code}` : 'Create Rack'}
      open={open}
      onClose={onClose}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <MuteText>Rack Preset</MuteText>
          <CenterDropdownStyle
            options={presetOptions}
            selection
            value={preset}
            onChange={(e, { value }) => setPreset(value)}
            placeholder="Preset"
            className="mute-dropdown"
            loading={presetLoading}
          />
        </div>
        <NumberFormField
          control={control}
          name="quantity"
          label="Quantity"
          error={errors.quantity}
          hint={errors.quantity?.message}
        />
        {isCreateNewRack && (
          <FormGroup widths="equal">
            {quantity > 1 ? (
              <Popup
                content="You cannot set a code when creating multiple racks. The system will generate unique codes for multi-rack creation."
                position="top center"
                trigger={
                  <ControlledTextFormField
                    control={control}
                    name="code"
                    label="Code"
                    disabled
                  />
                }
              />
            ) : (
              <ControlledTextFormField
                control={control}
                name="code"
                label="Code"
                error={errors.code}
                hint={errors.code?.message}
              />
            )}
          </FormGroup>
        )}
        <FormGroup widths="equal">
          <NumberFormField
            control={control}
            name="length"
            label={`Length (${warehouseData?.dimensions?.measurementUnit})`}
            maskOptions={decimalMask}
            error={errors.length}
            hint={errors.length?.message}
            disabled={!isCreateNewRack}
          />
          <NumberFormField
            control={control}
            name="width"
            label={`Width (${warehouseData?.dimensions?.measurementUnit})`}
            maskOptions={decimalMask}
            error={errors.width}
            hint={errors.width?.message}
            disabled={!isCreateNewRack}
          />
        </FormGroup>
        {isCreateNewRack && (
          <div className="d-flex">
            <CheckboxFormField
              name="createPreset"
              control={control}
              error={errors.createPreset}
              hint={errors.createPreset?.message}
            />
            <span>Save this Rack as a Preset</span>
          </div>
        )}
        <div className="d-flex justify-content-end">
          <HoverBorderButton type="button" onClick={onClose}>
            Cancel
          </HoverBorderButton>
          <StyledButton type="submit" loading={loading} className="ml-1">
            Create
          </StyledButton>
        </div>
      </Form>
    </SkupremeModal>
  );
}
